import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  Spinner,
} from "@learn-me/ui";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import {
  materialsApi,
  MATERIAL_CATEGORY_LABELS,
  type MaterialCategory,
  type MaterialInput,
} from "@/features/materials/api/materialsApi";

interface MaterialFormData {
  title: string;
  description: string;
  coverImageUrl: string;
  fileUrl: string;
  fileType: string;
  category: MaterialCategory;
  isFree: boolean;
  listPrice: number | null;
  salePrice: number;
  currency: string;
  isPublished: boolean;
}

export default function MaterialEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<MaterialFormData>({
    defaultValues: {
      category: "RECURSO",
      isFree: false,
      currency: "USD",
      isPublished: false,
      listPrice: null,
      salePrice: 0,
    },
  });

  const { data: material, isLoading } = useQuery({
    queryKey: ["admin-material", id],
    queryFn: () => materialsApi.getById(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (material) {
      reset({
        title: material.title,
        description: material.description ?? "",
        coverImageUrl: material.coverImageUrl ?? "",
        fileUrl: material.fileUrl ?? "",
        fileType: material.fileType ?? "",
        category: material.category,
        isFree: material.isFree,
        listPrice:
          material.listPrice != null ? Number(material.listPrice) : null,
        salePrice: Number(material.salePrice),
        currency: material.currency,
        isPublished: material.isPublished,
      });
    }
  }, [material, reset]);

  const buildPayload = (data: MaterialFormData): MaterialInput => {
    const hasDiscount =
      !data.isFree && data.listPrice != null && data.listPrice > data.salePrice;
    return {
      title: data.title,
      description: data.description || null,
      coverImageUrl: data.coverImageUrl || undefined,
      fileUrl: data.fileUrl,
      fileType: data.fileType || null,
      category: data.category,
      isFree: data.isFree,
      listPrice: hasDiscount ? data.listPrice : null,
      salePrice: data.isFree ? 0 : Number(data.salePrice),
      hasDiscount,
      currency: data.currency,
      isPublished: data.isPublished,
    };
  };

  const createMutation = useMutation({
    mutationFn: (data: MaterialFormData) =>
      materialsApi.create(buildPayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-materials"] });
      toast.success("Material creado");
      navigate("/admin/materials");
    },
    onError: () => toast.error("Error al crear el material"),
  });

  const updateMutation = useMutation({
    mutationFn: (data: MaterialFormData) =>
      materialsApi.update(id!, buildPayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-materials"] });
      toast.success("Material actualizado");
      navigate("/admin/materials");
    },
    onError: () => toast.error("Error al actualizar el material"),
  });

  const onSubmit = (data: MaterialFormData) => {
    if (!data.isFree && (!data.salePrice || data.salePrice <= 0)) {
      toast.error("Define un precio de venta o marca el material como gratis");
      return;
    }
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isFree = watch("isFree");

  if (isEditing && isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <Link
        to="/admin/materials"
        className="inline-flex items-center text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver a materiales
      </Link>

      <Card>
        <CardHeader>
          <h1 className="text-xl font-bold">
            {isEditing ? "Editar Material" : "Nuevo Material"}
          </h1>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <Input
              label="Título *"
              error={errors.title?.message}
              {...register("title", {
                required: "El título es requerido",
                minLength: { value: 3, message: "Mínimo 3 caracteres" },
              })}
            />

            <div>
              <label className="block text-sm font-medium mb-1">
                Descripción
              </label>
              <textarea
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                {...register("description")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Categoría
              </label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                {...register("category")}
              >
                {(
                  Object.keys(MATERIAL_CATEGORY_LABELS) as MaterialCategory[]
                ).map((c) => (
                  <option key={c} value={c}>
                    {MATERIAL_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="URL del archivo descargable *"
              placeholder="https://..."
              error={errors.fileUrl?.message}
              {...register("fileUrl", {
                required: "La URL del archivo es requerida",
              })}
            />
            <Input
              label="Tipo de archivo (PDF, ZIP, DOCX...)"
              {...register("fileType")}
            />
            <Input
              label="URL de imagen de portada"
              placeholder="https://..."
              {...register("coverImageUrl")}
            />

            <label className="flex items-center gap-2">
              <input type="checkbox" {...register("isFree")} />
              <span className="text-sm font-medium">Material gratuito</span>
            </label>

            {!isFree && (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  label="Precio de venta *"
                  error={errors.salePrice?.message}
                  {...register("salePrice", { valueAsNumber: true })}
                />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  label="Precio original (descuento)"
                  {...register("listPrice", {
                    setValueAs: (v) =>
                      v === "" || v === null || isNaN(Number(v))
                        ? null
                        : Number(v),
                  })}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Moneda</label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                {...register("currency")}
              >
                <option value="USD">USD</option>
                <option value="CRC">CRC</option>
              </select>
            </div>

            <label className="flex items-center gap-2">
              <input type="checkbox" {...register("isPublished")} />
              <span className="text-sm font-medium">
                Publicar (visible para usuarios)
              </span>
            </label>
          </CardContent>
          <div className="p-6 pt-0 flex gap-3">
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {isEditing ? "Guardar cambios" : "Crear material"}
            </Button>
            <Link to="/admin/materials">
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
